import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import {
  type Control,
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormSetValue,
} from 'react-hook-form';

import { CristaSpinner } from '@/components/Loaders';
import BrazilStateSelectField from '@/components/forms/BrazilStateSelectField';
import FormTextField from '@/components/forms/FormTextField';
import { usePostalCodeLookup } from '@/hooks/usePostalCodeLookup';
import { useTranslate } from '@/state/i18n';
import { type AddressFormValues } from '@/types/location';
import { BRAZIL_STATES, format } from '@/utils/data';

interface Props<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  /** Where the address object sits in the form values: `address`, `locations.0`. */
  name: Path<TFieldValues>;
}

const FILL_OPTIONS = { shouldDirty: true, shouldValidate: true } as const;

/**
 * Brazilian address (`AddressFormValues`, validated by `CommonValidationSchemas.address`): the CEP
 * fills street, neighborhood, city and UF; every field stays editable. Event "Onde" and offer
 * "Onde vale" use it.
 */
const AddressFields = <TFieldValues extends FieldValues>({
  control,
  setValue,
  name,
}: Props<TFieldValues>) => {
  const t = useTranslate('components.forms.AddressFields');
  const { lookupPostalCode, isLoading, error } = usePostalCodeLookup();
  const path = (key: keyof AddressFormValues) => `${name}.${key}` as Path<TFieldValues>;
  const fill = (key: keyof AddressFormValues, value: string) =>
    setValue(path(key), value as PathValue<TFieldValues, Path<TFieldValues>>, FILL_OPTIONS);

  const fillFromPostalCode = async (postalCode: string) => {
    const found = await lookupPostalCode(postalCode);
    if (!found) return;
    fill('street', found.street);
    fill('neighborhood', found.neighborhood ?? '');
    fill('city', found.city);
    if ((BRAZIL_STATES as readonly string[]).includes(found.state)) fill('state', found.state);
  };

  return (
    <Stack gap={2.5}>
      <Box sx={sx.postalCode}>
        <FormTextField
          control={control}
          name={path('postalCode')}
          label={t('postalCode')}
          optionalLabel={t('optional')}
          placeholder="58045-010"
          inputMode="numeric"
          hint={error ?? t('postalCodeHint')}
          formatOnBlur={(value) => format.postalCode(value)}
          onBlurValue={fillFromPostalCode}
          slotProps={{
            input: {
              endAdornment: isLoading ? (
                <InputAdornment position="end">
                  <CristaSpinner size={16} />
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
      </Box>
      <Box sx={sx.streetRow}>
        <FormTextField control={control} name={path('street')} label={t('street')} />
        <FormTextField
          control={control}
          name={path('number')}
          label={t('number')}
          optionalLabel={t('optional')}
        />
      </Box>
      <Box sx={sx.pair}>
        <FormTextField
          control={control}
          name={path('complement')}
          label={t('complement')}
          optionalLabel={t('optional')}
          placeholder={t('complementPlaceholder')}
        />
        <FormTextField
          control={control}
          name={path('neighborhood')}
          label={t('neighborhood')}
          optionalLabel={t('optional')}
        />
      </Box>
      <Box sx={sx.cityRow}>
        <FormTextField control={control} name={path('city')} label={t('city')} />
        <BrazilStateSelectField control={control} name={path('state')} label={t('state')} />
      </Box>
    </Stack>
  );
};

export default AddressFields;

const sx = {
  postalCode: { maxWidth: { sm: 240 } },
  streetRow: { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 140px' } },
  pair: { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } },
  cityRow: { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 100px', sm: '1fr 120px' } },
};
